export type CameraPoint = Readonly<{ x: number; y: number }>;
/** Screen-plane bounds: x across, y down the screen (world z − 0.625·height in the office). */
export type CameraBounds = Readonly<{ minX: number; maxX: number; minY: number; maxY: number }>;

/** Leave room for the header, floor chips and call dock in either renderer. */
export function cameraInsets(width: number, height: number) {
  return {
    top: Math.min(112, height * 0.24),
    bottom: Math.min(width <= 800 ? 216 : 104, height * 0.34),
    left: Math.min(24, width * 0.08),
    right: Math.min(24, width * 0.08),
  };
}

/** The camera centre that places the bounds' middle between the HUD insets at `scale` CSS px per unit. */
export function frameCenter(bounds: CameraBounds, scale: number, width: number, height: number): CameraPoint {
  const inset = cameraInsets(width, height);
  return {
    x: (bounds.minX + bounds.maxX) / 2 - (inset.left - inset.right) / (2 * scale),
    y: (bounds.minY + bounds.maxY) / 2 - (inset.top - inset.bottom) / (2 * scale),
  };
}

/** The largest scale that shows the whole bounds between the HUD insets, and its centre. */
export function fitCamera(bounds: CameraBounds, width: number, height: number) {
  const inset = cameraInsets(width, height);
  const scale = Math.min(
    Math.max(1, width - inset.left - inset.right) / (bounds.maxX - bounds.minX),
    Math.max(1, height - inset.top - inset.bottom) / (bounds.maxY - bounds.minY),
  );
  return { scale, center: frameCenter(bounds, scale, width, height) };
}

/** Bounded overscroll lets north-facing walls and labels clear the HUD. */
export function clampCameraFrame(
  center: CameraPoint,
  bounds: CameraBounds,
  viewWidth: number,
  viewHeight: number,
  screenWidth: number,
  screenHeight: number,
): CameraPoint {
  const inset = cameraInsets(screenWidth, screenHeight);
  const scale = screenHeight / viewHeight;
  const fit = frameCenter(bounds, scale, screenWidth, screenHeight);
  const minX = bounds.minX + viewWidth / 2 - inset.left / scale;
  const maxX = bounds.maxX - viewWidth / 2 + inset.right / scale;
  const minY = bounds.minY + viewHeight / 2 - inset.top / scale;
  const maxY = bounds.maxY - viewHeight / 2 + inset.bottom / scale;
  return {
    x: minX > maxX ? fit.x : Math.min(maxX, Math.max(minX, center.x)),
    y: minY > maxY ? fit.y : Math.min(maxY, Math.max(minY, center.y)),
  };
}

/** A fitted zoom may sit between the ordinary button/wheel steps. */
export function stepCameraZoom(current: number, levels: readonly number[], direction: 1 | -1): number {
  const ascending = levels[0]! < levels[levels.length - 1]!;
  const sign = ascending ? direction : -direction;
  let next = current;
  let distance = Infinity;
  for (const level of levels) {
    const delta = (level - current) * sign;
    if (delta > 0.001 && delta < distance) { next = level; distance = delta; }
  }
  return next;
}
